import React, { useState, useEffect, useCallback } from "react";
import * as api from "./api";
import { ToastProvider, useToast } from "./components/Toast";
import {
  DashboardIcon, ProjectsIcon, CompanyIcon, UsersIcon, SettingsIcon, BellIcon, InputIcon, OrganigramIcon, ReportIcon,
} from "./components/Icons";
import DashboardPage from "./pages/Dashboard";
import InputPage from "./pages/Input";
import ProjectsPage from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import { CompanyDetail } from "./pages/Companies";
import ReportPage from "./pages/Report";
import OrganigramPage from "./pages/Organigram";
import UserManagement from "./pages/UserManagement";
import SettingsPage from "./pages/Settings";
import LoginPage from "./pages/Login";
import "./styles.css";

const HistoryNavIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const CollapseIcon = ({ collapsed }) => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
       style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.25s ease" }}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

function GlobalHistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ users: [], project_ids: [], entity_types: [] });
  const [projects, setProjects] = useState([]);
  // Filter state
  const [filters, setFilters] = useState({
    user_id: "", project_id: "", variable_name: "", entity_type: "",
    since: "", until: "", limit: 200,
  });

  // Load facets + projects once for dropdown labels.
  useEffect(() => {
    Promise.all([
      api.getHistoryFacets().catch(() => ({ users: [], project_ids: [], entity_types: [] })),
      api.getProjects().catch(() => []),
    ]).then(([f, pj]) => {
      setFacets(f || { users: [], project_ids: [], entity_types: [] });
      setProjects(Array.isArray(pj) ? pj : []);
    });
  }, []);

  // Refetch history whenever filters change (small debounce-free for now).
  useEffect(() => {
    setLoading(true);
    api.getGlobalHistory(filters)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const projectName = (pid) => projects.find((p) => p.id === pid)?.name || pid;
  const setF = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const resetFilters = () => setFilters({
    user_id: "", project_id: "", variable_name: "", entity_type: "",
    since: "", until: "", limit: 200,
  });
  const hasActiveFilter = !!(filters.user_id || filters.project_id || filters.variable_name
    || filters.entity_type || filters.since || filters.until);

  return (
    <div className="fade-in">
      <div className="card">
        <div className="flex justify-between items-center mb-12" style={{ flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 className="heading fw-600" style={{ fontSize: 16 }}>Global Change History</h3>
            <p className="text-xs text-muted mt-4">All changes across all projects, users, and settings.</p>
          </div>
          {hasActiveFilter && (
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset filters</button>
          )}
        </div>

        {/* ── Filter strip ── */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10, marginBottom: 16,
          padding: 12, borderRadius: 10,
          background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)",
        }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>User</label>
            <select className="input select" value={filters.user_id}
              onChange={(e) => setF("user_id", e.target.value)}>
              <option value="">— Any user —</option>
              {facets.users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Project</label>
            <select className="input select" value={filters.project_id}
              onChange={(e) => setF("project_id", e.target.value)}>
              <option value="">— Any project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Variable name</label>
            <input className="input" type="text"
              placeholder="e.g. Room Revenue"
              value={filters.variable_name}
              onChange={(e) => setF("variable_name", e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Entity type</label>
            <select className="input select" value={filters.entity_type}
              onChange={(e) => setF("entity_type", e.target.value)}>
              <option value="">— Any —</option>
              {facets.entity_types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Since</label>
            <input className="input" type="datetime-local"
              value={filters.since}
              onChange={(e) => setF("since", e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Until</label>
            <input className="input" type="datetime-local"
              value={filters.until}
              onChange={(e) => setF("until", e.target.value)} />
          </div>
        </div>

        {loading && <div className="loading-container"><div className="spinner" /></div>}
        {!loading && history.length === 0 && (
          <p className="text-muted" style={{ padding: 20 }}>
            {hasActiveFilter ? "No changes match the current filters." : "No changes recorded yet."}
          </p>
        )}
        {!loading && history.length > 0 && (
          <p className="text-xxs text-dim mb-12">
            Showing {history.length} change{history.length === 1 ? "" : "s"}{hasActiveFilter ? " (filtered)" : ""}.
          </p>
        )}
        {!loading && history.map((h) => (
          <div key={h.id} style={{
            padding: "12px 16px", marginBottom: 4, borderRadius: 10,
            background: "rgba(255,255,255,0.02)",
            borderLeft: h.entity_type === "variable_value"
              ? "3px solid var(--accent)"
              : h.entity_type === "project" ? "3px solid var(--orange)" : "3px solid var(--text-dim)",
          }}>
            <div className="flex justify-between items-start">
              <div>
                <span className="text-sm fw-500">{h.changed_by_name || "System"}</span>
                <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                  {h.entity_type === "variable_value" && h.variable_name
                    ? `changed ${h.variable_name}${h.year ? ` (FY ${h.year})` : ""}`
                    : `${h.field_name} — ${h.entity_type}`}
                </span>
                {h.project_id && (
                  <span className="text-xxs text-dim" style={{ marginLeft: 8 }}>
                    · {projectName(h.project_id)}
                  </span>
                )}
              </div>
              <span className="text-xxs text-dim">
                {h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}
              </span>
            </div>
            {(h.old_value !== null || h.new_value !== null) && (
              <div className="flex gap-8 items-center mt-6">
                {h.old_value !== null && (
                  <span className="mono text-xs" style={{
                    background: "rgba(245,101,101,0.1)", color: "var(--red-light)",
                    padding: "2px 8px", borderRadius: 4, textDecoration: "line-through",
                  }}>{h.old_value}</span>
                )}
                <span className="text-xs text-muted">→</span>
                <span className="mono text-xs" style={{
                  background: "rgba(72,187,120,0.1)", color: "var(--green-light)",
                  padding: "2px 8px", borderRadius: 4,
                }}>{h.new_value}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function AppContent() {
  const [page, setPage] = useState("dashboard");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("fintrack:sidebarCollapsed") === "1"; } catch { return false; }
  });

  const handleNameLoaded = useCallback((name) => setSelectedProjectName(name), []);
  const handleCompanyNameLoaded = useCallback((name) => setSelectedCompanyName(name), []);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // Backend-reported total unread. The badge uses this so the count
  // keeps climbing even when there are >50 unread notifications (the
  // list endpoint caps at 50 for payload size).
  const [totalUnread, setTotalUnread] = useState(0);
  // Hydrate from api.js (which restored from localStorage on module load).
  const [currentUser, setCurrentUser] = useState(() => api.getCurrentSession());
  const toast = useToast();

  // Auto-logout on 401 from any API call. api.apiCall fires this event
  // after clearing the local session.
  useEffect(() => {
    const handler = () => setCurrentUser(null);
    window.addEventListener("fintrack:auth-expired", handler);
    return () => window.removeEventListener("fintrack:auth-expired", handler);
  }, []);

  // Not signed in → show the login screen. On success we set the user
  // and fall through to the normal app shell.
  if (!currentUser) {
    return <LoginPage onSuccess={(u) => setCurrentUser(u)} />;
  }

  const handleLogout = () => {
    api.logout();
    setCurrentUser(null);
    setPage("dashboard");
    setSelectedProjectId(null);
    setSelectedCompanyId(null);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("fintrack:sidebarCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const canEdit = currentUser.role === "Admin" || currentUser.role === "Edit";
  const isAdmin = currentUser.role === "Admin";

  const loadNotifications = useCallback(() => {
    api.getNotifications().then((res) => {
      // Accept either the new { items, total_unread } shape or the
      // legacy array shape (in case an older backend is still running).
      if (Array.isArray(res)) {
        setNotifications(res);
        setTotalUnread(res.filter((n) => !n.is_read).length);
      } else if (res && typeof res === "object") {
        setNotifications(res.items || []);
        setTotalUnread(res.total_unread || 0);
      }
    }).catch(() => {});
  }, []);

  // Prevent the mouse wheel from incrementing/decrementing focused number inputs.
  // The default browser behavior changes the value while scrolling the page,
  // which is almost never what the user wants in data-entry tables.
  useEffect(() => {
    const onWheel = (e) => {
      const t = e.target;
      if (t && t.tagName === "INPUT" && t.type === "number" && document.activeElement === t) {
        t.blur();
      }
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 15000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Badge counts use the backend's total, not the local list length.
  const unreadCount = totalUnread;
  const markRead = async (nid) => { await api.markNotificationRead(nid); loadNotifications(); };
  const markAllRead = async () => { await api.markAllNotificationsRead(); loadNotifications(); };

  const pages = [
    { id: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
    { id: "organigram", label: "Group Organigram", icon: <OrganigramIcon /> },
    { id: "input", label: "Input", icon: <InputIcon /> },
    { id: "projects", label: "Projects", icon: <ProjectsIcon /> },
    { id: "report", label: "Report", icon: <ReportIcon /> },
    ...(currentUser.role === "Admin" ? [{ id: "users", label: "User Mgmt", icon: <UsersIcon /> }] : []),
    { id: "history", label: "History", icon: <HistoryNavIcon /> },
    { id: "settings", label: "Settings", icon: <SettingsIcon /> },
  ];

  const navigateTo = (p) => {
    setPage(p);
    setSelectedProjectId(null);
    setSelectedProjectName("");
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const diff = (new Date() - d) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
    return d.toLocaleDateString();
  };

  const getPageTitle = () => {
    if (selectedProjectId) return selectedProjectName || "Project Detail";
    if (selectedCompanyId) return selectedCompanyName || "Company Detail";
    return pages.find((p) => p.id === page)?.label || "Dashboard";
  };

  return (
    <div className={`app-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      {/* SIDEBAR */}
      <aside className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-mark" aria-hidden="true">
              {/* Hotel / building mark — matches the favicon. */}
              <svg viewBox="0 0 64 64" width="100%" height="100%">
                <g fill="currentColor">
                  <rect x="14" y="18" width="14" height="32" rx="1.5" />
                  <rect x="30" y="12" width="20" height="38" rx="1.5" />
                  <rect x="18" y="22" width="3" height="3" /><rect x="22" y="22" width="3" height="3" />
                  <rect x="18" y="28" width="3" height="3" /><rect x="22" y="28" width="3" height="3" />
                  <rect x="18" y="34" width="3" height="3" /><rect x="22" y="34" width="3" height="3" />
                  <rect x="18" y="40" width="3" height="3" /><rect x="22" y="40" width="3" height="3" />
                  <rect x="34" y="17" width="3" height="3" /><rect x="39" y="17" width="3" height="3" /><rect x="44" y="17" width="3" height="3" />
                  <rect x="34" y="23" width="3" height="3" /><rect x="39" y="23" width="3" height="3" /><rect x="44" y="23" width="3" height="3" />
                  <rect x="34" y="29" width="3" height="3" /><rect x="39" y="29" width="3" height="3" /><rect x="44" y="29" width="3" height="3" />
                  <rect x="34" y="35" width="3" height="3" /><rect x="39" y="35" width="3" height="3" /><rect x="44" y="35" width="3" height="3" />
                  <rect x="38" y="42" width="4" height="8" />
                </g>
              </svg>
            </div>
            <div className="sidebar-logo-text">
              <h1>NeoHospFin</h1>
            </div>
          </div>
          <button className="sidebar-toggle" onClick={toggleSidebar}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <CollapseIcon collapsed={sidebarCollapsed} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {pages.map((p) => (
            <button key={p.id}
              className={`sidebar-item ${page === p.id && !selectedProjectId ? "active" : ""}`}
              onClick={() => navigateTo(p.id)}
              title={sidebarCollapsed ? p.label : undefined}>
              <span className="sidebar-item-icon">{p.icon}</span>
              <span className="sidebar-item-label">{p.label}</span>
            </button>
          ))}
        </nav>
        {/* Sidebar user row — name/role moved to the top-right header.
            Layout flips to a vertical stack when the sidebar is collapsed
            so the logout sits BELOW the avatar (instead of next to it). */}
        <div className="sidebar-user"
          title={`${currentUser.name} · ${currentUser.role}`}
          style={{
            flexDirection: sidebarCollapsed ? "column" : "row",
            gap: sidebarCollapsed ? 8 : 10,
            alignItems: "center",
          }}>
          <div className="sidebar-avatar">{(currentUser.name || "?").charAt(0)}</div>
          {!sidebarCollapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{currentUser.name}</div>
              <div className="text-xs text-muted">{currentUser.role}</div>
            </div>
          )}
          <button className="btn-icon"
            title="Sign out"
            onClick={handleLogout}
            style={{
              marginLeft: sidebarCollapsed ? 0 : "auto",
              color: "var(--text-muted)",
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        {/* Company logo — bottom-left corner of the sidebar. Hidden when
            the sidebar is collapsed so it doesn't squash. Same pill
            treatment as on the login page: white background extending
            past the wordmark so the logo sits in a balanced white box. */}
        {!sidebarCollapsed && (
          <div style={{
            marginTop: 10, paddingTop: 12,
            borderTop: "1px solid var(--border)",
            display: "flex", justifyContent: "center",
          }}>
            <div style={{
              width: "100%",
              background: "#ffffff",
              borderRadius: 8,
              padding: "4px 18px",
              display: "flex", justifyContent: "center", alignItems: "center",
              boxSizing: "border-box",
            }}>
              <img src="/ambuja-logo.jpg" alt="Ambuja Neotia"
                style={{
                  maxHeight: 42, maxWidth: "100%", objectFit: "contain",
                  display: "block",
                }} />
            </div>
          </div>
        )}
      </aside>

      {/* MAIN */}
      <div className="main-area">
        <header className="top-header">
          <h2>{getPageTitle()}</h2>
          {/* Right-side cluster: user identity then notifications. */}
          <div className="flex items-center" style={{ gap: 14 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <div className="text-right" style={{ lineHeight: 1.2 }}>
                <div className="text-sm fw-600" style={{ color: "var(--text-primary)" }}>
                  {currentUser.name}
                </div>
                <div className="text-xxs text-muted" style={{ letterSpacing: 0.4 }}>
                  {currentUser.role}
                </div>
              </div>
              <div className="sidebar-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
                {(currentUser.name || "?").charAt(0)}
              </div>
            </div>
          <div style={{ position: "relative" }}>
            <button className="notif-btn" onClick={() => setShowNotifications(!showNotifications)}>
              <BellIcon />
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </button>
            {showNotifications && (
              <div className="notif-panel fade-in">
                <div className="notif-header">
                  <h3 className="heading fw-600" style={{ fontSize: 15 }}>Notifications</h3>
                  <button className="btn btn-ghost btn-sm" onClick={markAllRead}>Mark all read</button>
                </div>
                <div className="notif-list">
                  {notifications.length === 0 && (
                    <div style={{ padding: 20, textAlign: "center" }}>
                      <p className="text-muted text-sm">No notifications yet</p>
                    </div>
                  )}
                  {notifications.map((n) => (
                    <div key={n.id} className={`notif-item ${n.is_read ? "" : "unread"}`}
                      onClick={() => markRead(n.id)}>
                      <div className="flex gap-10 items-start">
                        {!n.is_read && <div className="notif-dot" />}
                        <div>
                          <p style={{ fontSize: 13, lineHeight: 1.5, color: n.is_read ? "var(--text-muted)" : "var(--text-primary)" }}>
                            {n.message}
                          </p>
                          <p className="text-xs text-dim mt-4">{n.user_name} · {formatTime(n.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </header>

        <main className="content-area" onClick={() => showNotifications && setShowNotifications(false)}>
          {page === "dashboard" && !selectedProjectId && !selectedCompanyId && <DashboardPage />}
          {page === "organigram" && !selectedProjectId && !selectedCompanyId && (
            <OrganigramPage canEdit={canEdit} isAdmin={isAdmin} />
          )}
          {page === "input" && !selectedProjectId && !selectedCompanyId && (
            <InputPage canEdit={canEdit} />
          )}
          {page === "projects" && !selectedProjectId && !selectedCompanyId && (
            <ProjectsPage canEdit={canEdit} onSelectProject={(id, name) => { setSelectedProjectId(id); setSelectedProjectName(name); }} />
          )}
          {selectedProjectId && (
            <ProjectDetail projectId={selectedProjectId} canEdit={canEdit} isAdmin={isAdmin}
              onBack={() => { setSelectedProjectId(null); setSelectedProjectName(""); }}
              onNameLoaded={handleNameLoaded} />
          )}
          {page === "report" && !selectedCompanyId && !selectedProjectId && (
            <ReportPage />
          )}
          {selectedCompanyId && !selectedProjectId && (
            <CompanyDetail companyId={selectedCompanyId}
              onBack={() => { setSelectedCompanyId(null); setSelectedCompanyName(""); }}
              onNameLoaded={handleCompanyNameLoaded} />
          )}
          {page === "users" && !selectedProjectId && !selectedCompanyId && <UserManagement />}
          {page === "history" && !selectedProjectId && !selectedCompanyId && <GlobalHistoryPage />}
          {page === "settings" && !selectedProjectId && !selectedCompanyId && <SettingsPage />}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}